/**
 * PortfolioAnalyticsDashboard — the ultra-detailed executive Portfolio view.
 *
 * Modules (per /app/design_guidelines.json):
 *  1. Executive KPI Ribbon          – weighted SPI/CPI/SV/CV/HSE/Risks/CO
 *  2. Cognara AI Weekly Brief       – Cognara, 5 auto-bullets + refresh
 *  3. Interactive Geographic Map    – kept in the DEFAULT view (user request)
 *  4. Slippage Radar Quadrant       – SPI×CPI scatter with predicted risk tier
 *  5. Cost Overrun Forecast table   – BAC/EAC/VAC with sparklines
 *  6. Cumulative Cash-Flow S-Curve  – PV vs EV vs AC across the portfolio
 *  7. Milestone Timeline Ribbon     – next 3 milestones per project
 *  8. Resource Contention list      – cross-project over-allocations
 *  9. HSE / RAID / Change-Order roll-up cards
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { useTheme } from '@/contexts/ThemeContext';
import PortfolioMap from './PortfolioMap';
import {
  ResponsiveContainer, ScatterChart, Scatter, XAxis, YAxis, ZAxis, ReferenceLine,
  Tooltip, LineChart, Line, CartesianGrid, Legend, Area, AreaChart,
} from 'recharts';
import {
  Activity, TrendingDown, TrendingUp, AlertTriangle, ShieldAlert, GitPullRequest,
  MapPinned, RefreshCw, Sparkles, ArrowRight, Loader2, Users, ClipboardList,
  DollarSign, Target, Flag, Zap, ChevronDown, ChevronUp, ChevronRight, Send, MessageSquare,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { toast } from 'sonner';
import CreepDetailDrawer from '@/components/portfolio/CreepDetailDrawer';

// ─── Theme-aware design tokens ─────────────────────────────────────────────
const DARK = {
  appBg: '#060A12', card: '#0D1424', border: 'rgba(255,255,255,0.08)',
  primary: '#3B82F6', emerald: '#10B981', teal: '#0E7C6B', amber: '#F59E0B',
  crimson: '#EF4444', slate: '#64748B', text: '#F8FAFC', text2: '#CBD5E1',
  glow: 'rgba(30,78,140,0.25)', tooltipBg: '#0D1424', tooltipText: '#F8FAFC',
};
const LIGHT = {
  appBg: '#F8FAFC', card: '#FFFFFF', border: 'rgba(15,23,42,0.08)',
  primary: '#1E4E8C', emerald: '#059669', teal: '#0E7C6B', amber: '#D97706',
  crimson: '#DC2626', slate: '#64748B', text: '#0F172A', text2: '#334155',
  glow: 'rgba(30,78,140,0.10)', tooltipBg: '#FFFFFF', tooltipText: '#0F172A',
};

const fmtM = (v) => {
  const n = Number(v || 0);
  if (Math.abs(n) >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (Math.abs(n) >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
};

export default function PortfolioAnalyticsDashboard() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const T = isDark ? DARK : LIGHT;
  const spiTone = (v) => v >= 0.95 ? T.emerald : v >= 0.9 ? T.amber : T.crimson;
  const riskTone = { CRITICAL: T.crimson, HIGH: T.amber, MEDIUM: '#F59E0B99', LOW: T.emerald };

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [brief, setBrief] = useState(null);
  const [briefLoading, setBriefLoading] = useState(false);
  const [raidFeed, setRaidFeed] = useState([]);
  const [anomalies, setAnomalies] = useState([]);
  const [anomaliesLoading, setAnomaliesLoading] = useState(false);
  const [scheduleCreep, setScheduleCreep] = useState([]);
  const [scheduleCreepLoading, setScheduleCreepLoading] = useState(false);
  const [costCreepOpen, setCostCreepOpen] = useState(true);
  const [scheduleCreepOpen, setScheduleCreepOpen] = useState(true);
  const [filters, setFilters] = useState({ region: 'ALL', sector: 'ALL', risk: 'ALL' });
  // Collapsed by default per user request
  const [filterOpen, setFilterOpen] = useState(false);
  const [briefOpen,  setBriefOpen]  = useState(false);
  // L2 drill-down for a clicked creep alert row: {kind:'cost'|'schedule', target}
  const [creepDetail, setCreepDetail] = useState(null);

  // Q&A state for Cognara brief chat
  const [qaMessages, setQaMessages] = useState([]); // [{role, content}]
  const [qaInput, setQaInput] = useState('');
  const [qaLoading, setQaLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, rf] = await Promise.all([
        api.get('/portfolio/analytics/overview'),
        api.get('/portfolio/raid-feed?limit=20').catch(() => ({ data: { items: [] } })),
      ]);
      setData(ov.data);
      setRaidFeed(rf.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load portfolio analytics');
    } finally { setLoading(false); }
  }, []);

  // Cost-creep anomaly detection across every project the user can see.
  // Runs once the project list is loaded from the overview payload.
  const loadAnomalies = useCallback(async (projectIds) => {
    if (!projectIds?.length) return;
    setAnomaliesLoading(true);
    try {
      const results = await Promise.all(projectIds.map(pid =>
        api.get(`/v1/projects/${pid}/cc/cbs/anomalies?threshold_pct=90`)
           .then(r => (r.data.anomalies || []).map(a => ({ ...a, projectId: pid })))
           .catch(() => [])
      ));
      const flat = results.flat().sort((a, b) => b.utilisationPct - a.utilisationPct);
      setAnomalies(flat);
    } finally { setAnomaliesLoading(false); }
  }, []);

  // Schedule-creep: milestone slippage from the macro schedules of every
  // project in the portfolio. Uses the new `/portfolios/{pid}/schedule-creep`
  // endpoint which flattens `milestones` + `schedule_tasks(is_milestone)`.
  const loadScheduleCreep = useCallback(async () => {
    setScheduleCreepLoading(true);
    try {
      const r = await api.get('/portfolios/portfolio_001/schedule-creep?days_threshold=7');
      setScheduleCreep(r.data?.alerts || []);
    } catch { setScheduleCreep([]); }
    finally { setScheduleCreepLoading(false); }
  }, []);

  useEffect(() => {
    // Pull project ids from whatever key the overview payload exposes.
    const src = data?.projects || data?.portfolio?.projects || [];
    const pids = src.map(p => p.projectId || p.project_id || p.id).filter(Boolean);
    if (pids.length) loadAnomalies(pids);
    else {
      // Fallback: fetch the raw project list.
      api.get('/portfolio/projects').then(r => {
        const list = (r.data?.projects || r.data || []).map(p => p.projectId || p.project_id || p.id).filter(Boolean);
        if (list.length) loadAnomalies(list);
      }).catch(() => {});
    }
    // Schedule creep runs once regardless of project ids since the backend
    // handles portfolio scoping via `portfolio_id`.
    loadScheduleCreep();
  }, [data, loadAnomalies]);

  const runBrief = useCallback(async () => {
    setBriefLoading(true);
    try {
      const r = await api.post('/portfolio/executive-brief', {});
      setBrief(r.data);
      setBriefOpen(true);
      toast.success('Cognara brief refreshed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Brief generation failed');
    } finally { setBriefLoading(false); }
  }, []);

  // A generated brief costs a full Opus call — rehydrate the last saved one on
  // every mount so navigating away never loses it.
  useEffect(() => {
    let alive = true;
    api.get('/portfolio/executive-brief/latest')
      .then(r => {
        if (!alive || !r.data?.exists || !r.data?.brief) return;
        setBrief(r.data.brief);
        setBriefOpen(true);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const askCognara = useCallback(async (question) => {
    const q = (question || '').trim();
    if (!q) return;
    const newHistory = [...qaMessages, { role: 'user', content: q }];
    setQaMessages(newHistory);
    setQaInput('');
    setQaLoading(true);
    try {
      const r = await api.post('/portfolio/brief-qa', {
        question: q,
        history: qaMessages,
      });
      setQaMessages([...newHistory, { role: 'assistant', content: r.data.answer }]);
    } catch (e) {
      setQaMessages([...newHistory, { role: 'assistant', content: `⚠️ ${e?.response?.data?.detail || 'Cognara is unable to respond right now.'}` }]);
    } finally { setQaLoading(false); }
  }, [qaMessages]);

  useEffect(() => { load(); }, [load]);

  const kpi       = data?.kpi         || {};
  const allProjects = data?.projects  || [];
  const cashFlow  = data?.cash_flow   || [];
  const ribbon    = data?.milestone_ribbon || [];
  const resources = data?.resource_contention || [];

  // Filter rail: unique regions & sectors, plus risk tiers
  const regions = useMemo(() => Array.from(new Set(allProjects.map((p) => p.geography || 'Unknown').filter(Boolean))).sort(), [allProjects]);
  const sectors = useMemo(() => Array.from(new Set(allProjects.map((p) => p.sector || 'Unknown').filter(Boolean))).sort(), [allProjects]);
  const RISKS   = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];

  const projects = useMemo(() => allProjects.filter((p) => (
    (filters.region === 'ALL' || (p.geography || 'Unknown') === filters.region) &&
    (filters.sector === 'ALL' || (p.sector    || 'Unknown') === filters.sector) &&
    (filters.risk   === 'ALL' || p.risk_tier === filters.risk)
  )), [allProjects, filters]);

  const worst5 = useMemo(
    () => [...projects].sort((a, b) => (a.spi + a.cpi) - (b.spi + b.cpi)).slice(0, 5),
    [projects],
  );

  const cumFlow = useMemo(() => {
    let pv = 0, ev = 0, ac = 0;
    return cashFlow.map((c) => {
      pv += Number(c.pv || 0); ev += Number(c.ev || 0); ac += Number(c.ac || 0);
      return { date: (c.date || '').slice(0, 10), pv, ev, ac };
    });
  }, [cashFlow]);

  const projectsForMap = useMemo(() => projects.map((p) => ({
    id: p.id, project_id: p.id, name: p.name, lat: p.lat, lon: p.lon, lng: p.lon, latitude: p.lat, longitude: p.lon,
    progress: p.progress, spi: p.spi, cpi: p.cpi, bac: p.bac,
    hi_score: Math.round(((p.spi || 1) + (p.cpi || 1)) * 50),
    sector: p.sector,
    geography: p.geography,
    risk_tier: p.risk_tier,
    rag_status: p.risk_tier === 'CRITICAL' || p.risk_tier === 'HIGH' ? 'red' : p.risk_tier === 'MEDIUM' ? 'amber' : 'green',
  })), [projects]);

  return (
    <DashboardLayout>
      <div className="min-h-screen" style={{ background: T.appBg, color: T.text }} data-testid="portfolio-dashboard">
        <div className="p-6 space-y-4">

          {/* ── Header ─────────────────────────────────────────────────── */}
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.24em]" style={{ color: T.slate }}>Executive Portfolio</div>
              <h1 className="text-3xl font-extrabold tracking-tight">Global Delivery Cockpit</h1>
              <p className="text-sm" style={{ color: T.text2 }}>
                {kpi.total_projects || 0} projects · {kpi.critical_projects || 0} in critical-risk · updated {data ? new Date(data.generated_at).toLocaleTimeString() : '—'}
              </p>
            </div>
            <Button onClick={load} variant="outline" className="h-8 text-xs" data-testid="portfolio-refresh">
              <RefreshCw className="w-3 h-3 mr-1" /> Refresh
            </Button>
          </div>

          {/* ── Filter Rail (collapsible, collapsed by default) ────────── */}
          <div className="rounded-xl" data-testid="filter-rail" style={{ background: T.card, border: `1px solid ${T.border}` }}>
            <button onClick={() => setFilterOpen((o) => !o)} className="w-full flex items-center gap-2 px-3 py-2 text-left" data-testid="filter-rail-toggle">
              <span className="text-[10px] uppercase tracking-wider" style={{ color: T.slate }}>Filter</span>
              {(filters.region !== 'ALL' || filters.sector !== 'ALL' || filters.risk !== 'ALL') && (
                <span className="text-[9px] px-1.5 py-0.5 rounded-full font-mono"
                      style={{ background: T.primary + '22', color: T.primary }}>
                  {[filters.region, filters.sector, filters.risk].filter((v) => v !== 'ALL').join(' · ')}
                </span>
              )}
              <span className="ml-auto text-[10px] font-mono" style={{ color: T.text2 }}>
                {projects.length} of {allProjects.length} projects
              </span>
              {filterOpen ? <ChevronUp className="w-3.5 h-3.5" style={{ color: T.slate }} /> : <ChevronDown className="w-3.5 h-3.5" style={{ color: T.slate }} />}
            </button>
            {filterOpen && (
              <div className="flex flex-wrap items-center gap-2 px-3 pb-3 pt-1 border-t" style={{ borderColor: T.border }}>
                <ChipGroup label="Region" testKey="region" value={filters.region}
                           options={['ALL', ...regions]}
                           onChange={(v) => setFilters((f) => ({ ...f, region: v }))} T={T} />
                <ChipGroup label="Sector" testKey="sector" value={filters.sector}
                           options={['ALL', ...sectors]}
                           onChange={(v) => setFilters((f) => ({ ...f, sector: v }))} T={T} />
                <ChipGroup label="Risk"   testKey="risk"   value={filters.risk}
                           options={['ALL', ...RISKS]}
                           onChange={(v) => setFilters((f) => ({ ...f, risk: v }))} T={T} />
                {(filters.region !== 'ALL' || filters.sector !== 'ALL' || filters.risk !== 'ALL') && (
                  <button onClick={() => setFilters({ region: 'ALL', sector: 'ALL', risk: 'ALL' })}
                          className="text-[10px] underline ml-2"
                          style={{ color: T.primary }}
                          data-testid="filter-clear">Clear</button>
                )}
              </div>
            )}
          </div>

          {/* ── 1. KPI Ribbon ──────────────────────────────────────────── */}
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3" data-testid="kpi-ribbon">
            <KPI label="Weighted SPI" value={kpi.spi?.toFixed(3) ?? '—'} tone={spiTone(kpi.spi || 1)} icon={Target} testId="kpi-spi"  T={T}/>
            <KPI label="Weighted CPI" value={kpi.cpi?.toFixed(3) ?? '—'} tone={spiTone(kpi.cpi || 1)} icon={DollarSign} testId="kpi-cpi"  T={T}/>
            <KPI label="Schedule Var" value={fmtM(kpi.sv)} tone={(kpi.sv || 0) >= 0 ? T.emerald : T.crimson} icon={Activity} testId="kpi-sv" T={T} />
            <KPI label="Cost Var"     value={fmtM(kpi.cv)} tone={(kpi.cv || 0) >= 0 ? T.emerald : T.crimson} icon={TrendingDown} testId="kpi-cv" T={T} />
            <KPI label="HSE Incidents" value={kpi.hse_incidents ?? 0} tone={kpi.hse_incidents ? T.amber : T.emerald} icon={ShieldAlert} testId="kpi-hse"  T={T}/>
            <KPI label="Active Risks"  value={kpi.active_risks ?? 0}  tone={kpi.active_risks ? T.crimson : T.emerald} icon={AlertTriangle} testId="kpi-risks"  T={T}/>
            <KPI label="Open Changes"  value={kpi.open_change_orders ?? 0} tone={T.primary} icon={GitPullRequest} testId="kpi-change-orders"  T={T}/>
          </div>

          {/* ── 2. Cognara AI Executive Brief (sections + Q&A, collapsed by default) ── */}
          <Card testId="ai-executive-brief-card" T={T}>
            <div className="flex items-start justify-between">
              <button onClick={() => setBriefOpen((o) => !o)} className="flex items-center gap-2 text-left flex-1" data-testid="brief-toggle">
                <Sparkles className="w-4 h-4" style={{ color: T.primary }} />
                <div className="text-sm font-semibold">Cognara — Weekly Executive Brief</div>
                <span className="text-[9px] px-1.5 py-0.5 rounded font-mono" style={{ background: T.glow, color: T.text2 }}>Cognara</span>
                {brief?.generated_at && (
                  <span className="text-[9px] font-mono" style={{ color: T.slate }} data-testid="brief-generated-at">
                    saved · {new Date(brief.generated_at).toLocaleString()}
                  </span>
                )}
                {briefOpen ? <ChevronUp className="w-3.5 h-3.5 ml-1" style={{ color: T.slate }} /> : <ChevronDown className="w-3.5 h-3.5 ml-1" style={{ color: T.slate }} />}
              </button>
              <Button size="sm" onClick={runBrief} disabled={briefLoading} className="h-7 text-xs" data-testid="brief-refresh-button" style={{ background: T.primary, color: 'white' }}>
                {briefLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                {brief ? 'Refresh Brief' : 'Generate Brief'}
              </Button>
            </div>
            {briefOpen && (
              <div className="mt-3">
                {brief?.headline && (
                  <div className="text-xs mb-3 p-2.5 rounded-md" style={{ background: T.glow, color: T.text, borderLeft: `3px solid ${T.primary}` }} data-testid="brief-headline">
                    <span className="text-[9px] uppercase tracking-wider mr-2" style={{ color: T.slate }}>Headline</span>
                    {brief.headline}
                  </div>
                )}
                {brief ? (
                  Array.isArray(brief.sections) && brief.sections.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="brief-sections">
                      {brief.sections.map((sec, i) => (
                        <div key={sec.id || i}
                             className="rounded-lg p-3 border"
                             style={{ background: T.appBg, borderColor: T.border }}
                             data-testid={`brief-section-${sec.id || i}`}>
                          <div className="flex items-center gap-2 mb-1.5">
                            <span className="w-1.5 h-1.5 rounded-full" style={{ background: T.primary }} />
                            <div className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: T.text }}>{sec.title}</div>
                          </div>
                          {sec.summary && <div className="text-xs mb-2 leading-relaxed" style={{ color: T.text2 }}>{sec.summary}</div>}
                          {Array.isArray(sec.metrics) && sec.metrics.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mb-2">
                              {sec.metrics.map((m, j) => {
                                const tone = m.tone === 'red' ? T.crimson : m.tone === 'amber' ? T.amber : m.tone === 'green' ? T.emerald : T.primary;
                                return (
                                  <div key={j} className="px-2 py-1 rounded text-[10px] font-mono"
                                       style={{ background: tone + '18', color: tone, border: `1px solid ${tone}40` }}
                                       data-testid={`brief-metric-${i}-${j}`}>
                                    <span style={{ opacity: 0.7 }}>{m.label}:</span> <strong>{m.value}</strong>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          {Array.isArray(sec.insights) && sec.insights.length > 0 && (
                            <ul className="space-y-1 text-xs">
                              {sec.insights.map((ins, k) => (
                                <li key={k} className="flex items-start gap-1.5 leading-relaxed">
                                  <span className="mt-1 w-1 h-1 rounded-full shrink-0" style={{ background: T.primary }} />
                                  <span style={{ color: T.text2 }}>{ins}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <ol className="space-y-1 text-xs">
                      {brief.bullets?.map((b, i) => (
                        <li key={i} data-testid={`brief-bullet-${i}`} className="flex items-start gap-2 leading-relaxed">
                          <span className="mt-0.5" style={{ color: T.primary }}>▸</span>
                          <span style={{ color: T.text2 }}>{b}</span>
                        </li>
                      ))}
                    </ol>
                  )
                ) : (
                  <div className="text-xs italic" style={{ color: T.slate }}>Click <b>Generate Brief</b> to run Cognara across the portfolio.</div>
                )}

                {/* ── Cognara Q&A Chat ─────────────────────────────────── */}
                {brief && (
                  <div className="mt-4 pt-3 border-t" style={{ borderColor: T.border }} data-testid="brief-qa-panel">
                    <div className="flex items-center gap-2 mb-2">
                      <MessageSquare className="w-3.5 h-3.5" style={{ color: T.primary }} />
                      <div className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: T.text }}>
                        Ask Cognara
                      </div>
                      {qaMessages.length > 0 && (
                        <button onClick={() => setQaMessages([])} className="ml-auto text-[10px] underline" style={{ color: T.slate }} data-testid="qa-clear">
                          Clear
                        </button>
                      )}
                    </div>

                    {qaMessages.length === 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-2" data-testid="qa-suggestions">
                        {[
                          'Which projects need CEO attention this week?',
                          'What is driving the schedule variance?',
                          'Where should I redirect capital next quarter?',
                          'Any hidden HSE or contract risks?',
                        ].map((sug, i) => (
                          <button key={i}
                            onClick={() => askCognara(sug)}
                            disabled={qaLoading}
                            className="text-[10px] px-2 py-1 rounded-full border"
                            style={{ borderColor: T.border, color: T.text2, background: T.appBg }}
                            data-testid={`qa-suggestion-${i}`}>
                            {sug}
                          </button>
                        ))}
                      </div>
                    )}

                    {qaMessages.length > 0 && (
                      <div className="space-y-2 mb-2 max-h-64 overflow-auto pr-1" data-testid="qa-messages">
                        {qaMessages.map((m, i) => (
                          <div key={i}
                            className="rounded-lg px-3 py-2 text-xs leading-relaxed"
                            style={{
                              background: m.role === 'user' ? T.primary + '18' : T.appBg,
                              border: `1px solid ${m.role === 'user' ? T.primary + '40' : T.border}`,
                              color: T.text,
                            }}
                            data-testid={`qa-msg-${m.role}-${i}`}>
                            <div className="text-[9px] uppercase tracking-wider mb-1" style={{ color: T.slate }}>
                              {m.role === 'user' ? 'You' : 'Cognara'}
                            </div>
                            <div style={{ whiteSpace: 'pre-wrap', color: T.text2 }}>{m.content}</div>
                          </div>
                        ))}
                        {qaLoading && (
                          <div className="flex items-center gap-2 text-xs px-3 py-2" style={{ color: T.slate }} data-testid="qa-thinking">
                            <Loader2 className="w-3 h-3 animate-spin" /> Cognara is analysing the portfolio…
                          </div>
                        )}
                      </div>
                    )}

                    <form
                      onSubmit={(e) => { e.preventDefault(); askCognara(qaInput); }}
                      className="flex items-center gap-2">
                      <input
                        type="text"
                        value={qaInput}
                        onChange={(e) => setQaInput(e.target.value)}
                        placeholder="Ask about SPI, CPI, top risks, worst projects…"
                        disabled={qaLoading}
                        className="flex-1 h-8 px-3 rounded-md text-xs outline-none"
                        style={{ background: T.appBg, border: `1px solid ${T.border}`, color: T.text }}
                        data-testid="qa-input"
                      />
                      <Button type="submit" size="sm" disabled={qaLoading || !qaInput.trim()} className="h-8 px-3 text-xs" style={{ background: T.primary, color: 'white' }} data-testid="qa-send">
                        {qaLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                      </Button>
                    </form>
                  </div>
                )}
              </div>
            )}
          </Card>

          {/* ── 2b. Cognara Creep Alerts — Cost + Schedule (side-by-side, collapsible) ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card testId="cognara-cost-creep-card" T={T}>
              <button
                type="button"
                onClick={() => setCostCreepOpen(v => !v)}
                className="w-full flex items-center gap-2 text-left cursor-pointer hover:opacity-90 select-none"
                style={{ marginBottom: costCreepOpen ? '0.75rem' : 0 }}
                data-testid="cost-creep-toggle"
                aria-expanded={costCreepOpen}
              >
                {costCreepOpen ? <ChevronDown className="w-4 h-4" style={{ color: T.slate }} /> : <ChevronRight className="w-4 h-4" style={{ color: T.slate }} />}
                <AlertTriangle className="w-4 h-4" style={{ color: T.crimson }} />
                <div className="text-sm font-semibold">Cognara — Cost Creep Alerts</div>
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ml-1"
                  style={{ background: `${T.crimson}18`, color: T.crimson }}>
                  {anomaliesLoading ? '…' : `${anomalies.length} leaves ≥ 90%`}
                </span>
                <span className="text-[10px] ml-auto" style={{ color: T.slate }}>Click a row for L2 detail</span>
              </button>
              {costCreepOpen && (
                anomaliesLoading ? (
                  <div className="text-xs py-4 text-center" style={{ color: T.slate }}>Scanning CBS leaves…</div>
                ) : anomalies.length === 0 ? (
                  <div className="text-xs py-4 text-center" style={{ color: T.slate }}>
                    No leaf cost codes have crossed the 90% actual/budget threshold. Nice work.
                  </div>
                ) : (
                  <div className="max-h-[240px] overflow-y-auto pr-1" data-testid="cognara-anomaly-list">
                    {anomalies.slice(0, 20).map((a) => {
                      const sevColor = a.severity === 'CRITICAL' ? T.crimson : a.severity === 'HIGH' ? T.crimson : T.amber;
                      return (
                        <button key={`${a.projectId}-${a.costCode}`}
                          type="button"
                          onClick={() => setCreepDetail({ kind: 'cost', target: a })}
                          className="w-full flex items-center gap-3 py-2 border-b text-[11px] text-left transition-colors hover:brightness-110"
                          style={{ borderColor: T.border, background: 'transparent' }}
                          title="Open L2 detail"
                          data-testid={`anomaly-${a.projectId}-${a.costCode}`}>
                          <span className="font-mono px-1.5 py-0.5 rounded"
                            style={{ background: `${sevColor}18`, color: sevColor, minWidth: 62, textAlign: 'center' }}>
                            {a.utilisationPct.toFixed(0)}%
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="truncate" style={{ color: T.text }}>{a.costCodeName || a.costCode}</div>
                            <div className="text-[10px]" style={{ color: T.slate }}>
                              {a.projectId} · {a.costCode} · {a.costCategory || '—'}
                            </div>
                          </div>
                          <div className="text-right" style={{ color: T.text }}>
                            <div className="font-mono">{fmtM(a.actual)}</div>
                            <div className="text-[10px]" style={{ color: T.slate }}>of {fmtM(a.budget)}</div>
                          </div>
                          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full"
                            style={{ background: `${sevColor}20`, color: sevColor }}>
                            {a.severity}
                          </span>
                          <ChevronRight className="w-3.5 h-3.5" style={{ color: T.slate }} />
                        </button>
                      );
                    })}
                    {anomalies.length > 20 && (
                      <div className="text-[10px] text-center py-2" style={{ color: T.slate }}>
                        +{anomalies.length - 20} more anomalies. Drill into CBS Matrix to investigate.
                      </div>
                    )}
                  </div>
                )
              )}
            </Card>

            {/* ── Cognara — Schedule Creep Alerts (macro-schedule milestones) ── */}
            <Card testId="cognara-schedule-creep-card" T={T}>
              <button
                type="button"
                onClick={() => setScheduleCreepOpen(v => !v)}
                className="w-full flex items-center gap-2 text-left cursor-pointer hover:opacity-90 select-none"
                style={{ marginBottom: scheduleCreepOpen ? '0.75rem' : 0 }}
                data-testid="schedule-creep-toggle"
                aria-expanded={scheduleCreepOpen}
              >
                {scheduleCreepOpen ? <ChevronDown className="w-4 h-4" style={{ color: T.slate }} /> : <ChevronRight className="w-4 h-4" style={{ color: T.slate }} />}
                <AlertTriangle className="w-4 h-4" style={{ color: T.amber }} />
                <div className="text-sm font-semibold">Cognara — Schedule Creep Alerts</div>
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ml-1"
                  style={{ background: `${T.amber}20`, color: T.amber }}>
                  {scheduleCreepLoading ? '…' : `${scheduleCreep.length} milestones slipping`}
                </span>
                <span className="text-[10px] ml-auto" style={{ color: T.slate }}>Click a row for L2 detail</span>
              </button>
              {scheduleCreepOpen && (
                scheduleCreepLoading ? (
                  <div className="text-xs py-4 text-center" style={{ color: T.slate }}>Scanning macro schedules…</div>
                ) : scheduleCreep.length === 0 ? (
                  <div className="text-xs py-4 text-center" style={{ color: T.slate }}>
                    No milestones slipping beyond 7 days across the portfolio. Great cadence.
                  </div>
                ) : (
                  <div className="max-h-[240px] overflow-y-auto pr-1" data-testid="schedule-creep-list">
                    {scheduleCreep.slice(0, 20).map((a, i) => {
                      const sevColor = a.severity === 'CRITICAL' ? T.crimson : a.severity === 'HIGH' ? T.crimson : T.amber;
                      return (
                        <button key={`${a.projectId}-${a.milestoneId}-${i}`}
                          type="button"
                          onClick={() => setCreepDetail({ kind: 'schedule', target: a })}
                          className="w-full flex items-center gap-3 py-2 border-b text-[11px] text-left transition-colors hover:brightness-110"
                          style={{ borderColor: T.border, background: 'transparent' }}
                          title="Open L2 detail"
                          data-testid={`schedule-creep-${a.projectId}-${a.milestoneId}`}>
                          <span className="font-mono px-1.5 py-0.5 rounded"
                            style={{ background: `${sevColor}18`, color: sevColor, minWidth: 62, textAlign: 'center' }}>
                            +{a.slipDays}d
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="truncate" style={{ color: T.text }}>{a.milestoneName || '—'}</div>
                            <div className="text-[10px]" style={{ color: T.slate }}>
                              {a.projectName || a.projectId} · {a.phase}
                            </div>
                          </div>
                          <div className="text-right" style={{ color: T.text }}>
                            <div className="font-mono text-[10px]">{a.forecastDate}</div>
                            <div className="text-[10px]" style={{ color: T.slate }}>bl {a.baselineDate}</div>
                          </div>
                          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full"
                            style={{ background: `${sevColor}20`, color: sevColor }}>
                            {a.severity}
                          </span>
                          <ChevronRight className="w-3.5 h-3.5" style={{ color: T.slate }} />
                        </button>
                      );
                    })}
                    {scheduleCreep.length > 20 && (
                      <div className="text-[10px] text-center py-2" style={{ color: T.slate }}>
                        +{scheduleCreep.length - 20} more milestones slipping. Open Macro Schedule to investigate.
                      </div>
                    )}
                  </div>
                )
              )}
            </Card>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            <Card testId="portfolio-interactive-map" className="lg:col-span-3 p-0 overflow-hidden flex flex-col" T={T}>
              <div className="p-3 border-b flex items-center gap-2" style={{ borderColor: T.border }}>
                <MapPinned className="w-4 h-4" style={{ color: T.primary }} />
                <span className="text-sm font-semibold">Geographic Delivery Map</span>
                <span className="text-[10px] ml-auto" style={{ color: T.slate }}>{projects.length} sites</span>
              </div>
              <div className="w-full flex-1" style={{ minHeight: 480, height: 480, position: 'relative' }}>
                <PortfolioMap projects={projectsForMap} loading={loading} isDark={isDark} />
              </div>
            </Card>

            <Card testId="slippage-radar-card" className="lg:col-span-2 flex flex-col" T={T}>
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-4 h-4" style={{ color: T.amber }} />
                <span className="text-sm font-semibold">Slippage Radar</span>
                <span className="text-[10px] ml-auto" style={{ color: T.slate }}>X: SPI · Y: CPI · size = BAC</span>
              </div>
              <div className="flex-1 min-h-[440px]" data-testid="radar-scatter-plot">
                <ResponsiveContainer width="100%" height="100%">
                  <ScatterChart margin={{ top: 12, right: 20, bottom: 40, left: 8 }}>
                    <CartesianGrid stroke={T.border} strokeDasharray="3 3" />
                    <XAxis type="number" dataKey="spi" name="SPI" domain={[0.5, 1.5]} tick={{ fill: T.text2, fontSize: 10 }} />
                    <YAxis type="number" dataKey="cpi" name="CPI" domain={[0.5, 1.5]} tick={{ fill: T.text2, fontSize: 10 }} />
                    <ZAxis type="number" dataKey="bac" range={[80, 700]} />
                    <ReferenceLine x={1} stroke={T.slate} strokeDasharray="4 4" />
                    <ReferenceLine y={1} stroke={T.slate} strokeDasharray="4 4" />
                    <Tooltip
                      cursor={{ strokeDasharray: '3 3' }}
                      // eslint-disable-next-line react/no-unstable-nested-components
                      content={({ active, payload }) => {
                        if (!active || !payload || !payload.length) return null;
                        const p = payload[0].payload || {};
                        const spiC = p.spi >= 0.95 ? T.emerald : p.spi >= 0.85 ? T.amber : T.crimson;
                        const cpiC = p.cpi >= 0.95 ? T.emerald : p.cpi >= 0.85 ? T.amber : T.crimson;
                        return (
                          <div style={{ background: T.tooltipBg, border: `1px solid ${T.border}`, borderRadius: 8, padding: '8px 10px', color: T.tooltipText, boxShadow: '0 4px 16px rgba(0,0,0,0.25)', minWidth: 200 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 4, color: T.text }} data-testid="radar-tooltip-name">{p.name || 'Project'}</div>
                            {p.sector && <div style={{ fontSize: 9, color: T.slate, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{p.sector}</div>}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, fontSize: 11 }}>
                              <div><span style={{ color: T.slate }}>SPI:</span> <strong style={{ color: spiC }}>{Number(p.spi || 0).toFixed(3)}</strong></div>
                              <div><span style={{ color: T.slate }}>CPI:</span> <strong style={{ color: cpiC }}>{Number(p.cpi || 0).toFixed(3)}</strong></div>
                              <div><span style={{ color: T.slate }}>BAC:</span> <strong>{fmtM(p.bac)}</strong></div>
                              <div><span style={{ color: T.slate }}>Risk:</span> <strong style={{ color: riskTone[p.risk_tier] || T.slate }}>{p.risk_tier || '—'}</strong></div>
                            </div>
                          </div>
                        );
                      }}
                    />
                    {['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map((tier) => (
                      <Scatter key={tier} name={tier}
                        data={projects.filter((p) => p.risk_tier === tier).map((p) => ({ ...p, testid: `radar-${p.id}` }))}
                        fill={riskTone[tier]} fillOpacity={0.8} />
                    ))}
                    <Legend wrapperStyle={{ fontSize: 10, color: T.text2, paddingTop: 8 }} verticalAlign="bottom" iconSize={9}/>
                  </ScatterChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>

          {/* ── 5 + 6. Cost Overrun + Cash Flow ────────────────────────── */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            <Card testId="cost-overrun-table" className="lg:col-span-3" T={T}>
              <div className="flex items-center gap-2 mb-2">
                <TrendingDown className="w-4 h-4" style={{ color: T.crimson }} />
                <span className="text-sm font-semibold">Cost Overrun Forecast — Top 8</span>
              </div>
              <div className="overflow-auto">
                <table className="w-full text-xs">
                  <thead style={{ color: T.slate }}>
                    <tr className="text-left border-b" style={{ borderColor: T.border }}>
                      <th className="py-1 pr-2">Project</th>
                      <th className="py-1 pr-2 text-right">BAC</th>
                      <th className="py-1 pr-2 text-right">EAC</th>
                      <th className="py-1 pr-2 text-right">VAC</th>
                      <th className="py-1 pr-2 text-right">CPI</th>
                      <th className="py-1 text-right">Trend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...projects].sort((a, b) => a.vac - b.vac).slice(0, 8).map((p) => (
                      <tr key={p.id} className="border-b hover:bg-white/5 cursor-pointer" style={{ borderColor: T.border }}
                          onClick={() => navigate(`/projects/${p.id}`)} data-testid={`cost-row-${p.id}`}>
                        <td className="py-1.5 pr-2 truncate max-w-[220px]">{p.name}</td>
                        <td className="py-1.5 pr-2 text-right font-mono" style={{ color: T.text2 }}>{fmtM(p.bac)}</td>
                        <td className="py-1.5 pr-2 text-right font-mono" style={{ color: T.text2 }}>{fmtM(p.eac)}</td>
                        <td className="py-1.5 pr-2 text-right font-mono font-semibold" style={{ color: p.vac >= 0 ? T.emerald : T.crimson }}>{fmtM(p.vac)}</td>
                        <td className="py-1.5 pr-2 text-right font-mono" style={{ color: spiTone(p.cpi) }}>{p.cpi.toFixed(3)}</td>
                        <td className="py-1.5 text-right">
                          <Sparkline value={p.cpi} color={spiTone(p.cpi)} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>

            <Card testId="cash-flow-curve" className="lg:col-span-2" T={T}>
              <div className="flex items-center gap-2 mb-2">
                <Activity className="w-4 h-4" style={{ color: T.primary }} />
                <span className="text-sm font-semibold">Cumulative Cash-Flow</span>
                <span className="text-[10px] ml-auto" style={{ color: T.slate }}>Portfolio · $</span>
              </div>
              <div className="h-[300px]">
                <ResponsiveContainer>
                  <AreaChart data={cumFlow} margin={{ top: 8, right: 10, left: 0, bottom: 6 }}>
                    <defs>
                      <linearGradient id="gPV" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%"  stopColor={T.primary} stopOpacity={0.35} />
                        <stop offset="100%" stopColor={T.primary} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke={T.border} strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fill: T.slate, fontSize: 9 }} minTickGap={40}/>
                    <YAxis tickFormatter={fmtM} tick={{ fill: T.slate, fontSize: 9 }}/>
                    <Tooltip contentStyle={{ background: T.tooltipBg, border: `1px solid ${T.border}`, borderRadius: 8, color: T.tooltipText, boxShadow: '0 4px 16px rgba(0,0,0,0.2)' }} labelStyle={{ color: T.tooltipText }} itemStyle={{ color: T.tooltipText }} formatter={(v) => fmtM(v)}/>
                    <Legend wrapperStyle={{ fontSize: 10, color: T.text2 }}/>
                    <Area type="monotone" dataKey="pv" name="Planned Value" stroke={T.primary}  fill="url(#gPV)" strokeWidth={1.5}/>
                    <Line type="monotone" dataKey="ev" name="Earned Value"  stroke={T.emerald} strokeWidth={2} dot={false}/>
                    <Line type="monotone" dataKey="ac" name="Actual Cost"   stroke={T.amber}   strokeWidth={2} dot={false}/>
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>

          {/* ── 7. Milestone Ribbon ────────────────────────────────────── */}
          <Card testId="milestone-ribbon-card" T={T}>
            <div className="flex items-center gap-2 mb-2">
              <Flag className="w-4 h-4" style={{ color: T.primary }} />
              <span className="text-sm font-semibold">Next Milestones · 12-week window</span>
            </div>
            <div className="max-h-[240px] overflow-auto">
              <table className="w-full text-xs">
                <tbody>
                  {ribbon.slice(0, 10).map((row) => (
                    <tr key={row.project_id} className="border-b" style={{ borderColor: T.border }}>
                      <td className="py-1.5 pr-4 truncate max-w-[240px]" style={{ color: T.text2 }}>{row.name}</td>
                      <td className="py-1.5">
                        <div className="flex flex-wrap gap-1.5">
                          {(row.milestones || []).length === 0
                            ? <span className="text-[10px] italic" style={{ color: T.slate }}>No upcoming milestones</span>
                            : row.milestones.map((m) => (
                                <span key={m.id} className="text-[10px] px-2 py-0.5 rounded-full font-mono flex items-center gap-1"
                                      style={{ background: m.status === 'DONE' ? T.emerald + '22' : T.primary + '22', color: m.status === 'DONE' ? T.emerald : T.text2 }}
                                      data-testid={`milestone-${m.id}`}>
                                  ◆ {m.name?.slice(0, 26)} · {(m.date || '').slice(0, 10)}
                                </span>
                              ))
                          }
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* ── 8 + 9. Resource Contention + Worst-5 Projects ─────────── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card testId="resource-contention-card" T={T}>
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-4 h-4" style={{ color: T.amber }} />
                <span className="text-sm font-semibold">Resource Contention</span>
                <span className="text-[10px] ml-auto" style={{ color: T.slate }}>{resources.length} over-allocated</span>
              </div>
              {resources.length === 0 ? (
                <div className="text-xs italic p-2" style={{ color: T.slate }}>No cross-project over-allocations detected.</div>
              ) : (
                <div className="space-y-1 max-h-[220px] overflow-auto">
                  {resources.slice(0, 10).map((r) => (
                    <div key={r.resource_id} className="flex items-center gap-2 text-xs py-1 border-b" style={{ borderColor: T.border }}
                         data-testid={`contention-${r.resource_id}`}>
                      <span className="flex-1 truncate" title={(r.projects || []).join(', ')}>
                        {r.name}
                        <span className="ml-1 text-[9px]" style={{ color: T.slate }}>
                          {r.type} · {r.task_count} tasks{r.peak_date ? ` · peak ${String(r.peak_date).slice(0, 10)}` : ''}
                        </span>
                      </span>
                      <span className="font-mono" style={{ color: T.text2 }}>{r.assigned}/{r.max_units}</span>
                      <span className="font-mono font-semibold" style={{ color: T.crimson }}>+{r.over_by_pct}%</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <Card testId="critical-projects-card" T={T}>
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4" style={{ color: T.crimson }} />
                <span className="text-sm font-semibold">Highest-Risk Projects</span>
              </div>
              <div className="space-y-1">
                {worst5.map((p) => (
                  <div key={p.id} className="flex items-center gap-2 text-xs py-1 border-b cursor-pointer hover:bg-white/5" style={{ borderColor: T.border }}
                       onClick={() => navigate(`/projects/${p.id}`)} data-testid={`worst-${p.id}`}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: riskTone[p.risk_tier] || T.slate }} />
                    <span className="flex-1 truncate">{p.name}</span>
                    <span className="font-mono w-14 text-right" style={{ color: spiTone(p.spi) }}>SPI {p.spi.toFixed(2)}</span>
                    <span className="font-mono w-14 text-right" style={{ color: spiTone(p.cpi) }}>CPI {p.cpi.toFixed(2)}</span>
                    <ArrowRight className="w-3 h-3" style={{ color: T.slate }} />
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* ── RAID Feed + HSE/RAID/CO strip ─────────────────────────── */}
          <Card testId="raid-feed-card" T={T}>
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4" style={{ color: T.crimson }} />
              <span className="text-sm font-semibold">Live RAID Feed — highest-severity open items</span>
              <span className="text-[10px] ml-auto" style={{ color: T.slate }}>{raidFeed.length} shown</span>
            </div>
            {raidFeed.length === 0 ? (
              <div className="text-xs italic p-2" style={{ color: T.slate }}>No high-severity open RAID items right now.</div>
            ) : (
              <div className="max-h-[260px] overflow-auto">
                <table className="w-full text-xs">
                  <thead style={{ color: T.slate }}>
                    <tr className="text-left border-b" style={{ borderColor: T.border }}>
                      <th className="py-1 pr-2">Type</th>
                      <th className="py-1 pr-2">Title</th>
                      <th className="py-1 pr-2">Project</th>
                      <th className="py-1 pr-2">Severity</th>
                      <th className="py-1 pr-2">Score</th>
                      <th className="py-1 pr-2">Owner</th>
                      <th className="py-1 pr-2">Due</th>
                    </tr>
                  </thead>
                  <tbody>
                    {raidFeed.map((r) => (
                      <tr key={r.id} className="border-b hover:bg-white/5 cursor-pointer" style={{ borderColor: T.border }}
                          onClick={() => navigate(`/projects/${r.project_id}`)}
                          data-testid={`raid-feed-row-${r.id}`}>
                        <td className="py-1.5 pr-2">
                          <span className="text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider font-mono"
                                style={{ background: T.primary + '22', color: T.text2 }}>{r.type}</span>
                        </td>
                        <td className="py-1.5 pr-2 truncate max-w-[280px]" style={{ color: T.text }}>{r.title}</td>
                        <td className="py-1.5 pr-2 truncate max-w-[180px]" style={{ color: T.text2 }}>{r.project_name}</td>
                        <td className="py-1.5 pr-2">
                          <span className="text-[9px] px-1.5 py-0.5 rounded font-mono font-semibold uppercase"
                                style={{ background: (r.severity === 'CRITICAL' ? T.crimson : T.amber) + '22',
                                         color:      r.severity === 'CRITICAL' ? T.crimson : T.amber }}>
                            {r.severity}
                          </span>
                        </td>
                        <td className="py-1.5 pr-2 font-mono" style={{ color: T.text2 }}>{r.risk_score ?? '—'}</td>
                        <td className="py-1.5 pr-2 font-mono text-[10px]" style={{ color: T.text2 }}>{r.owner || '—'}</td>
                        <td className="py-1.5 pr-2 font-mono text-[10px]" style={{ color: T.slate }}>{(r.due_date || '').slice(0, 10)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {/* ── HSE / RAID / CO summary strip ─────────────────────────── */}
          <div className="grid grid-cols-3 gap-4">
            <MiniStat icon={ShieldAlert} label="HSE Incidents (portfolio)" value={kpi.hse_incidents ?? 0} tone={T.crimson} testId="hse-rollup"  T={T}/>
            <MiniStat icon={ClipboardList} label="Open High Risks (RAID)" value={kpi.active_risks ?? 0} tone={T.amber} testId="raid-rollup"  T={T}/>
            <MiniStat icon={GitPullRequest} label="Open Change Orders" value={kpi.open_change_orders ?? 0} tone={T.primary} testId="co-rollup"  T={T}/>
          </div>

        </div>
      </div>

      {creepDetail && (
        <CreepDetailDrawer
          kind={creepDetail.kind}
          target={creepDetail.target}
          T={T}
          onClose={() => setCreepDetail(null)}
          onNavigate={(path) => { setCreepDetail(null); navigate(path); }}
        />
      )}
    </DashboardLayout>
  );
}

// ─── Building blocks (theme-aware; receive `T` as a prop) ────────────────
function Card({ children, className = '', testId, T }) {
  return (
    <div className={`rounded-xl p-3 ${className}`}
         style={{ background: T.card, border: `1px solid ${T.border}`, boxShadow: `0 8px 32px ${T.glow}` }}
         data-testid={testId}>
      {children}
    </div>
  );
}

function KPI({ label, value, tone, icon: Icon, testId, T }) {
  return (
    <div className="rounded-xl p-3" style={{ background: T.card, border: `1px solid ${T.border}` }} data-testid={testId}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-3.5 h-3.5" style={{ color: tone }} />
        <span className="text-[9px] uppercase tracking-wider" style={{ color: T.slate }}>{label}</span>
      </div>
      <div className="text-xl font-mono font-bold" style={{ color: tone }}>{value}</div>
    </div>
  );
}

function MiniStat({ icon: Icon, label, value, tone, testId, T }) {
  return (
    <div className="rounded-xl p-3 flex items-center gap-3" style={{ background: T.card, border: `1px solid ${T.border}` }} data-testid={testId}>
      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: tone + '22' }}>
        <Icon className="w-4 h-4" style={{ color: tone }} />
      </div>
      <div>
        <div className="text-[9px] uppercase tracking-wider" style={{ color: T.slate }}>{label}</div>
        <div className="text-lg font-mono font-bold" style={{ color: T.text }}>{value}</div>
      </div>
    </div>
  );
}

function Sparkline({ value, color }) {
  const pts = Array.from({ length: 8 }, (_, i) => 4 + Math.sin(i * 0.9 + value) * 3 + (value - 1) * 4);
  const w = 60, h = 12;
  const path = pts.map((y, i) => `${i === 0 ? 'M' : 'L'}${(i / (pts.length - 1)) * w},${h - y}`).join(' ');
  return <svg width={w} height={h}><path d={path} fill="none" stroke={color} strokeWidth={1.4} /></svg>;
}

function ChipGroup({ label, options, value, onChange, testKey, T }) {
  return (
    <div className="flex items-center gap-1 flex-wrap">
      <span className="text-[9px] uppercase tracking-wider mr-1" style={{ color: T.slate }}>{label}</span>
      {options.map((opt) => {
        const active = value === opt;
        return (
          <button
            key={opt}
            onClick={() => onChange(opt)}
            className="text-[10px] px-2 py-0.5 rounded-full font-mono transition-colors"
            style={{
              background: active ? T.primary : 'transparent',
              border:     `1px solid ${active ? T.primary : T.border}`,
              color:      active ? '#fff' : T.text2,
            }}
            data-testid={`chip-${testKey}-${String(opt).replace(/\s+/g, '_')}`}
          >
            {opt === 'ALL' ? 'All' : opt}
          </button>
        );
      })}
    </div>
  );
}
